// Shared mic-capture settings used by every recorder in the app (verses,
// letters). Centralized so a quality fix in one place benefits all of them
// instead of silently drifting apart.

// Tuned constraints — noise suppression / echo cancellation / auto-gain and a
// speech-appropriate sample rate, instead of leaving getUserMedia() to pick
// whatever the browser/device defaults to (which is often noticeably worse).
export function getMicConstraints() {
  return {
    audio: {
      echoCancellation: { ideal: true },
      noiseSuppression: { ideal: true },
      autoGainControl: { ideal: true },
      sampleRate: { ideal: 44100 },
      channelCount: { ideal: 1 },
    },
  };
}

// Prefer MP4/AAC: Chrome and iOS Safari both support it, and it decodes
// cleanly via decodeAudioData → WAV. WebM/Opus is the fallback for Firefox;
// Chrome's live-stream WebM is unreliable with decodeAudioData.
const PREFERRED_MIME_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mpeg',
];

export function chooseMimeType() {
  return PREFERRED_MIME_TYPES.find(t => MediaRecorder.isTypeSupported(t)) || '';
}

// Duration via an <audio> element rather than decodeAudioData — the latter
// cannot reliably decode the fragmented MP4 that iOS Safari's MediaRecorder
// produces (silent/garbled playback), which is why nothing in this app
// decodes recorded audio client-side. Metadata-only reads are unaffected.
//
// A freshly recorded/chunked blob commonly reports duration = Infinity until
// something seeks near the end (a known MediaRecorder/Chrome quirk — the
// container's duration box isn't finalized). We force that seek here so
// callers always get a real number instead of having to work around it.
export function getAudioDuration(blob) {
  return new Promise((resolve) => {
    if (!blob || blob.size === 0) {
      console.warn('getAudioDuration: blob is empty, resolving 0');
      resolve(0);
      return;
    }
    const url = URL.createObjectURL(blob);
    const audio = new Audio();
    // Some browsers only fire timeupdate/seeked reliably for elements that
    // are actually in the document — keep it out of view but attached.
    audio.style.display = 'none';
    document.body.appendChild(audio);
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(safetyTimer);
      audio.remove();
      URL.revokeObjectURL(url);
      resolve(value);
    };
    // Guard against a workaround that never fires in some browser/blob
    // combinations — never hang the caller indefinitely.
    const safetyTimer = setTimeout(() => {
      console.warn('getAudioDuration: timed out waiting for duration; resolving 0', { size: blob.size, type: blob.type });
      finish(0);
    }, 4000);
    audio.onloadedmetadata = () => {
      console.log(`getAudioDuration: loadedmetadata — duration=${audio.duration}`);
      if (audio.duration === Infinity || isNaN(audio.duration)) {
        // Seeking past the end forces the browser to walk the file and
        // discover the true duration; it clamps to the actual end time.
        audio.currentTime = 1e9;
        audio.ontimeupdate = () => {
          audio.ontimeupdate = null;
          console.log(`getAudioDuration: after seek — duration=${audio.duration}`);
          finish(isFinite(audio.duration) ? audio.duration : 0);
        };
      } else {
        finish(audio.duration);
      }
    };
    audio.onerror = () => {
      console.warn('getAudioDuration: audio element error', audio.error);
      finish(0);
    };
    audio.src = url;
  });
}
